import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@sunfish/lib/utils';
import { Spinner } from './spinner';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				brand: 'bg-brand text-brand-foreground shadow hover:bg-brand/90',
				default: 'bg-blue text-blue-foreground shadow hover:bg-blue/90',
				destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
				// Transparent, not bg-background: an outline button is a border on
				// whatever surface it sits on. Painting the page background is wrong
				// anywhere the two differ - inside a card, and on the branded
				// registration pages, where BrandedStyle repoints .bg-background at
				// the event's canvas color and turned this into a dark block on a
				// light card. Inheriting the text color keeps the label readable on
				// whichever surface that turns out to be.
				outline: 'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline'
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-md px-3 text-xs',
				xs: 'h-6 rounded-md px-2 text-xs',
				lg: 'h-10 rounded-md px-6',
				xl: 'h-12 rounded-lg px-8 text-base',
				icon: 'h-9 w-9',
				inline: 'h-auto px-2 text-xs leading-none'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, isLoading, children, ...props }, ref) => {
		const classes = cn(buttonVariants({ variant, size, className }));

		// Slot merges onto its immediate child, so the wrapper below would take the
		// button styles for itself and leave the caller's element — nearly always a
		// Link — a bare inline anchor. Tailwind renders svg as display:block, so an
		// icon then broke onto its own line above the label, and the padding sat on
		// the wrapper rather than the anchor, leaving most of the button unclickable.
		if (asChild) {
			return (
				<Slot className={classes} ref={ref} {...props}>
					{children}
				</Slot>
			);
		}

		return (
			<button className={classes} ref={ref} {...props}>
				<div className="flex gap-2">
					{isLoading && <Spinner />}
					<span className="grow flex items-center justify-center gap-2">{children}</span>
				</div>
			</button>
		);
	}
);
Button.displayName = 'Button';

export { Button, buttonVariants };
